import classnames from "classnames";
import { observer } from 'mobx-react';
import moment from "moment";

import { tabs } from "pages/profile/utilities/tabs";
import React, { useEffect } from 'react';

import { COLORS, ICONS, ROUTES } from "shared/constants"
import { useAsyncEffect, useMedia, useNavigate, useStore } from "shared/hooks";
import { LayoutBody, LayoutContent } from 'shared/layout';
import { UserDashboardModel, UserDashboardResponseModel, UserDashboardWidgetModel } from 'shared/models';
import { ApplicationModule } from 'shared/modules';
import { userDashboardsGetQuery } from "shared/queries";
import { UiButton, UiIcon, UiTabs } from "shared/uikit";
import { getGroupedWidgets } from './utilities/getGroupedWidgets';
import { DashboardPeriod, IUserDashboardGroup, UserDashboardContentItemModel } from 'shared/models/UserDashboardModel.ts';
import { IUserDashboardResponseModel } from 'shared/models';

import { PPDWidget } from "./components/PPDWidget";
import { PPDBonuses } from 'pages/profile-dashboard/components/PPDBonuses';

import './styles.scss';

import { DashboardDictProvider } from 'pages/profile-dashboard/context/useDashboardDict';
import { generateConsultantDashboard, generateDesignerDashboard, generateManagerDashboard, generateTerritorialDashboard } from '../../mocks/dashboards/generateDashboard';
import { PPDWidgetPieChart} from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetPieChart';
import { PPDWidgetProgress } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetProgress';
import { PPDWidgetProductGroupsPrices } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetProductGroupsPrices';
import { PPDWidgetLeagueStatus } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetLeagueStatus';
import { PPDWidgetLeagueRating } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetLeagueRating';
import { PPDWidgetCalendar } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetCalendar';
import { PPDWidgetWorkdaysLeft } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetWorkdaysLeft';
import { PPDWidgetLeagueInfoModal } from 'pages/profile-dashboard/components/PPDWidget/components/PPDWidgetLeagueInfoModal';

export const ProfileDashboardsDesignerPage = observer(() => {
    const navigate = useNavigate();
    const { is320, is768, is1024 } = useMedia()

    const store = useStore(() => ({
        userDashboard: new UserDashboardModel(),
        contentTab: 'month' as DashboardPeriod,
        view: 'card',
        isOpenBonuses: false,
        isOpenLeagueInfoModal: false
    }));

    useEffect(() => {
        ApplicationModule.setTitle('Личный кабинет');
    });

    useEffect(() => {
        if (is320 || is768) {
            store.set("view", 'card');
        }
    }, [is768, is320, store]);

    const dashboard = generateDesignerDashboard();

    useAsyncEffect(async () => {
        // let response;

        const response = {
            isSuccess: true,
            data: dashboard.data as unknown as UserDashboardResponseModel
        };

        // response = await userDashboardsGetQuery();

        if (response.isSuccess && response.data?.item?.content) {
            store.set('userDashboard', new UserDashboardModel(response.data.item.content));
            if (store.userDashboard.content.month) {
                store.set("contentTab", "month");
            } else if (store.userDashboard.content.quarter) {
                store.set("contentTab", "quarter");
            } else if (store.userDashboard.content.year) {
                store.set("contentTab", "year");
            }
        }
    }, []);

    const content = store.userDashboard.content[store.contentTab] || null;
    const bonuses = store.userDashboard.bonuses

    const { isMobileOrTablet} = useMedia()

    const openBonuses = () => {
        store.set("isOpenBonuses", true);
    }

    const closeBonuses = () => {
        store.set("isOpenBonuses", false);
    }

    const calendarWidget = content?.widgets.find(widget => widget.type_id === "calendar");
    const updatedAt = store.userDashboard?.updated_at;
    const updatedAtTime = updatedAt ? moment(updatedAt).format("HH:mm,") : null;
    const updatedAtDate = updatedAt ? moment(updatedAt).format("D MMMM") : null;

    return (
        <>
            <LayoutBody title='Дашборд'>
                <LayoutContent className={'ppd--layout'}>
                    {content && (
                        <DashboardDictProvider
                            value={{
                                stores: store.userDashboard.stores || [],
                                employees: store.userDashboard.employees || [],
                                positions: store.userDashboard.positions || [],
                                periods: store.userDashboard.periods || [],
                                groups: store.userDashboard.groups || {},
                                units: store.userDashboard.units || {},
                                assortment: store.userDashboard.assortment || []
                            }}
                        >
                            <div className={'ppd'}>
                                <div className="ppd__title-box">
                                    <h1 className="ppd__title">Дашборд</h1>
                                    {isMobileOrTablet && updatedAt && (
                                        <div className="ppd__update">
                                            <UiIcon icon={ICONS.HISTORY} size={16} color="#2C2F35" className="ppd__update-icon"/>
                                            <div className="ppd__update-text">
                                                <span className="ppd__update-main">
                                                    <span className="ppd__update-label">Данные актуальны на</span>
                                                    <span className="ppd__update-time">{updatedAtTime}</span>
                                                </span>
                                                <span className="ppd__update-date">{updatedAtDate}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="ppd__nav-bar">
                                    <div className="ppd__header">
                                        <UiTabs
                                            className={'ppd__header-tabs'}
                                            onChange={store.handleChange}
                                            name={'contentTab'}
                                            items={store.userDashboard.tabs}
                                            value={store.contentTab}
                                            variant="filled"
                                        />

                                        {!isMobileOrTablet && updatedAt && (
                                            <div className="ppd__update">
                                                <UiIcon icon={ICONS.HISTORY} size={16} color="#2C2F35" className="ppd__update-icon"/>
                                                <div className="ppd__update-text">
                                                    <span className="ppd__update-main">
                                                        <span className="ppd__update-label">Данные актуальны на</span>
                                                        <span className="ppd__update-time">{updatedAtTime}</span>
                                                    </span>
                                                    <span className="ppd__update-date">{updatedAtDate}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {
                                        store.contentTab === "month" && calendarWidget && (
                                            <div className="ppd__schedule">
                                                <PPDWidgetCalendar
                                                    widget={calendarWidget}
                                                />
                                                <PPDWidgetWorkdaysLeft
                                                    widget={calendarWidget}
                                                />
                                            </div>
                                        )
                                    }
                                </div>
                                <div className="ppd__widgets">
                                    {getGroupedWidgets(content).map((group) => (
                                        <div key={group.id} className={classnames("ppd__group", `ppd__group--${group.id}`)}>
                                            {group.name && <h3 className="ppd__group-title">{group.name}</h3>}
                                            <div className="ppd__group-widgets">
                                                {group.widgets.map((widget, index) => (
                                                    <div className={classnames("ppd__widget", {
                                                        "ppd__widget--base": widget.type_id === 'base',
                                                        "ppd__widget--compare": widget.type_id === 'compare',
                                                        "ppd__widget--progress": widget.type_id === 'progress',
                                                        "ppd__widget--league-status": widget.type_id === 'league_status',
                                                        "ppd__widget--line-chart": widget.type_id === 'line_chart',
                                                        "ppd__widget--league-rating": widget.type_id === 'league_rating',
                                                        "ppd__widget--product-groups-prices": widget.type_id === 'product_groups_prices',
                                                        "ppd__widget--pie-chart": widget.type_id === 'pie_chart',
                                                    })} key={widget.name + index}>
                                                        <PPDWidget
                                                            key={widget.name + index}
                                                            widget={widget}
                                                            indexWidget={index}
                                                            onOpenLeagueInfo={() => store.set('isOpenLeagueInfoModal', true)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {
                                    bonuses?.items?.length > 0 && store.contentTab === 'month' && (
                                        <div className="ppd__bonuses">
                                            <button
                                                type={"button"}
                                                className="ppd__bonuses-button"
                                                onClick={openBonuses}
                                            >
                                                <UiIcon icon={ICONS.BONUS_GOLD} size={32}/>
                                                <span className="ppd__bonuses-button-text">
                                                    Ваши бонусы
                                                    <UiIcon icon={ICONS.CHEVRON_DOWN} size={15} className="ppd__bonuses-button-icon"/>
                                                </span>
                                            </button>
                                        </div>
                                    )
                                }
                            </div>
                            {
                                store.contentTab === 'month' && (
                                    <PPDBonuses
                                        groups={bonuses.groups || []}
                                        items={bonuses.items || [] }
                                        total={bonuses.total}
                                        isOpen={store.isOpenBonuses}
                                        onClose={closeBonuses}
                                    />
                                )
                            }
                        </DashboardDictProvider>
                    )}
                </LayoutContent>
            </LayoutBody>
            <PPDWidgetLeagueInfoModal
                isOpen={store.isOpenLeagueInfoModal}
                onClose={() => store.set("isOpenLeagueInfoModal", false)}
            />
        </>
    );
});
